import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Mail } from 'lucide-react';
import { PageLoadingIndicator } from '@kioskgaming/page-loading';
import { formatInTimezone, useTimezone } from '@kioskgaming/ui';
import AppLayout from '../components/layout/AppLayout';
import {
  ADMIN_MESSAGES_LABEL,
  ADMIN_MESSAGES_PATH,
  adminMessagePath,
  announceAdminMessagesChange,
} from '../components/adminMessages/adminMessages';
import {
  fetchAdminMessage,
  fetchAdminMessages,
  type AdminMessageDetail,
  type AdminMessageSummary,
} from '../services/api';

const PAGE_SIZE = 20;

function Header() {
  return (
    <div className="mb-4">
      <h1 className="flex items-center gap-2 text-xl font-semibold text-stone-900">
        <Mail className="h-5 w-5 text-emerald-600" aria-hidden /> {ADMIN_MESSAGES_LABEL}
      </h1>
      <p className="mt-1 text-sm text-stone-600">
        Messages the Vault Play admin team sent your Shop. They also arrive by email when your Shop has one.
      </p>
    </div>
  );
}

function MessageList() {
  const { timezone } = useTimezone();
  const [items, setItems] = useState<AdminMessageSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetchAdminMessages({ page, limit: PAGE_SIZE })
      .then((res) => {
        if (!alive) return;
        if (res.success && res.data) {
          setItems(res.data.items);
          setTotal(res.data.total);
          setFailed(false);
        } else setFailed(true);
      })
      .catch(() => alive && setFailed(true))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [page]);

  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  if (loading) return <PageLoadingIndicator />;
  if (failed) return <p className="text-sm text-red-700">Could not load admin messages. Try again in a moment.</p>;
  if (!items.length) {
    return <p className="text-sm text-stone-500">No messages yet. When the admin team writes to your Shop, it shows here.</p>;
  }

  return (
    <>
      <ul className="divide-y divide-stone-200 overflow-hidden rounded-lg border border-stone-200 bg-white" aria-label="Admin messages">
        {items.map((m) => (
          <li key={m.id}>
            <Link to={adminMessagePath(m.id)} className="flex items-start gap-3 px-4 py-3 hover:bg-stone-50">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <span className={m.read ? 'font-medium text-stone-800' : 'font-semibold text-stone-900'}>{m.subject}</span>
                  {!m.read ? (
                    <span className="rounded-full bg-emerald-600 px-2 py-0.5 text-xs font-semibold text-white">New</span>
                  ) : null}
                </div>
                <p className="mt-1 truncate text-sm text-stone-600">{m.preview}</p>
              </div>
              <span className="shrink-0 text-xs text-stone-500">{m.sentAt ? formatInTimezone(m.sentAt, timezone) : ''}</span>
            </Link>
          </li>
        ))}
      </ul>
      {pages > 1 ? (
        <div className="mt-3 flex items-center justify-between text-sm">
          <button
            type="button"
            className="rounded-lg border border-stone-300 bg-white px-3 py-1.5 text-stone-700 hover:bg-stone-50 disabled:opacity-50"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >
            Newer
          </button>
          <span className="text-stone-500">
            Page {page} of {pages}
          </span>
          <button
            type="button"
            className="rounded-lg border border-stone-300 bg-white px-3 py-1.5 text-stone-700 hover:bg-stone-50 disabled:opacity-50"
            disabled={page >= pages}
            onClick={() => setPage((p) => p + 1)}
          >
            Older
          </button>
        </div>
      ) : null}
    </>
  );
}

function MessageView({ id }: { id: string }) {
  const { timezone } = useTimezone();
  const [message, setMessage] = useState<AdminMessageDetail | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'missing' | 'failed'>('loading');

  useEffect(() => {
    let alive = true;
    setState('loading');
    fetchAdminMessage(id)
      .then((res) => {
        if (!alive) return;
        if (res.success && res.data) {
          setMessage(res.data);
          setState('ready');
          announceAdminMessagesChange();
        } else setState('failed');
      })
      .catch((error: { response?: { status?: number } }) => {
        if (alive) setState(error?.response?.status === 404 ? 'missing' : 'failed');
      });
    return () => {
      alive = false;
    };
  }, [id]);

  return (
    <div>
      <Link to={ADMIN_MESSAGES_PATH} className="mb-3 inline-flex items-center gap-1 text-sm font-medium text-emerald-700 hover:text-emerald-800">
        <ArrowLeft className="h-4 w-4" aria-hidden /> All admin messages
      </Link>
      {state === 'loading' ? <PageLoadingIndicator /> : null}
      {state === 'missing' ? <p className="text-sm text-stone-600">This message was not found.</p> : null}
      {state === 'failed' ? <p className="text-sm text-red-700">Could not load the message. Try again in a moment.</p> : null}
      {state === 'ready' && message ? (
        <article className="rounded-lg border border-stone-200 bg-white p-5" aria-label={message.subject}>
          <h2 className="text-lg font-semibold text-stone-900">{message.subject}</h2>
          <p className="mt-1 text-xs text-stone-500">
            From the Vault Play team{message.sentAt ? ` · ${formatInTimezone(message.sentAt, timezone)}` : ''}
          </p>
          <div className="mt-4 whitespace-pre-wrap break-words text-sm leading-relaxed text-stone-800">{message.body}</div>
        </article>
      ) : null}
    </div>
  );
}

/** Shop portal › Admin messages: the list, or one message when the URL names it. */
export default function AdminMessagesPage() {
  const { messageId } = useParams<{ messageId?: string }>();
  return (
    <AppLayout>
      <Header />
      {messageId ? <MessageView id={messageId} /> : <MessageList />}
    </AppLayout>
  );
}
